import { useEffect, useState } from 'react';
import { fetchOrdersDailyAvailability } from '../services/ordersDailyTruthClient';
import type { OrdersDailyAvailability } from '../utils/ordersDailySupport';

/** How long to wait before asking again after the check failed. */
const RETRY_MS = 30_000;

/**
 * The server's answer to "does this org get Order Flow Intelligence?"
 * (`GET /orders/v1/availability`). `null` until answered; a failed check is
 * retried and stays `null` meanwhile, so a brief outage never drops an org
 * onto the older view. Skipped in demo mode (demo always shows the board).
 */
export function useOrdersDailyAvailability(opts: { enabled: boolean }) {
  const [availability, setAvailability] = useState<OrdersDailyAvailability | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opts.enabled) return undefined;
    const ac = new AbortController();
    let timer: number | null = null;
    const load = async () => {
      try {
        const result = await fetchOrdersDailyAvailability({ signal: ac.signal });
        if (ac.signal.aborted) return;
        setAvailability(result);
        setError(null);
      } catch (err) {
        if (ac.signal.aborted) return;
        setError(err instanceof Error ? err.message : String(err));
        timer = window.setTimeout(() => { void load(); }, RETRY_MS);
      }
    };
    void load();
    return () => {
      ac.abort();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [opts.enabled]);

  return { availability, error };
}
